import { useState, memo, useContext, useEffect, useRef } from "react";
import styles from './homepage.module.css';
import { FaSearch } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { AllDataContext } from "../Context/Context";
import { getHotelSuggestions } from "../services/api";

const HeroSection = () => {
  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const { globalData } = useContext(AllDataContext);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  useEffect(() => {
    const query = inputValue.trim();
    if (query.length < 2) {
      setSuggestions([]);
      setSuggestionsOpen(false);
      setSuggestionsLoading(false);
      return undefined;
    }

    let active = true;
    setSuggestionsLoading(true);
    const timer = setTimeout(async () => {
      const results = await getHotelSuggestions(query);
      if (!active) return;
      setSuggestions(results);
      // Opened even when empty, so an address like "1335 6th St" says it found
      // nothing instead of the dropdown silently never appearing.
      setSuggestionsOpen(true);
      setSuggestionsLoading(false);
      setActiveSuggestion(-1);
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue]);

  useEffect(() => {
    const closeSuggestions = (event) => {
      if (!searchRef.current?.contains(event.target)) setSuggestionsOpen(false);
    };
    document.addEventListener("mousedown", closeSuggestions);
    return () => document.removeEventListener("mousedown", closeSuggestions);
  }, []);

  // "California" as free text matches hotels *named* California — including ones
  // in Louisiana and Nevada — instead of hotels in the state. When the query
  // names a state, browse that state instead of text-searching for it.
  const matchStateName = (value) => {
    const query = String(value || "").trim().toLowerCase();
    if (!query) return null;
    const states = Array.isArray(globalData?.states) ? globalData.states : [];
    return states.find(
      (state) =>
        state?.name?.toLowerCase() === query || state?.abbreviation?.toLowerCase() === query,
    ) || null;
  };

  const browseState = (abbreviation) => {
    setSuggestionsOpen(false);
    navigate(`/city?id=${encodeURIComponent(abbreviation)}`);
  };

  const handleSearch = (selectedValue) => {
    const query = String(typeof selectedValue === "string" ? selectedValue : inputValue).trim();
    if (!query) return;

    const state = matchStateName(query);
    if (state?.abbreviation) return browseState(state.abbreviation);

    setSuggestionsOpen(false);
    navigate(`/city?query=${encodeURIComponent(query)}`);
  };

  const selectSuggestion = (suggestion) => {
    setInputValue(suggestion.value);
    setSuggestionsOpen(false);

    // The dropdown already knows what this row is, so trust it rather than
    // re-deriving from the text.
    if (suggestion.type === "state" && suggestion.secondary) {
      return browseState(suggestion.secondary);
    }

    // Picking "California, MD" must list hotels in that town, not every hotel
    // with "California" in its name — so the city is passed as a scope rather
    // than as search text. The state disambiguates the many same-named towns.
    if (suggestion.type === "city") {
      const params = new URLSearchParams({ city: suggestion.value });
      if (suggestion.secondary) params.set("state", suggestion.secondary);
      return navigate(`/city?${params.toString()}`);
    }

    // Hotel rows stay a text search, deliberately skipping the state-name
    // shortcut in handleSearch.
    navigate(`/city?query=${encodeURIComponent(suggestion.value)}`);
  };

  // A city and a state can share a name ("New York"), so each row spells out
  // what it is. Cities carry their state abbreviation, hotels their address.
  const suggestionLabel = (suggestion) =>
    suggestion.type === "city" && suggestion.secondary
      ? `${suggestion.label}, ${suggestion.secondary}`
      : suggestion.label;

  const suggestionMeta = (suggestion) => {
    if (suggestion.type === "hotel") return suggestion.secondary;
    return suggestion.type === "city" ? "City" : "State";
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown" && suggestionsOpen) {
      event.preventDefault();
      setActiveSuggestion((current) => Math.min(current + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp" && suggestionsOpen) {
      event.preventDefault();
      setActiveSuggestion((current) => Math.max(current - 1, 0));
    } else if (event.key === "Escape") {
      setSuggestionsOpen(false);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (suggestionsOpen && activeSuggestion >= 0) {
        selectSuggestion(suggestions[activeSuggestion]);
      } else {
        handleSearch();
      }
    }
  };

  return (
    <div className={styles.full_screen}>
      <div className={styles.text_center}>
        <h1 className={styles.text_4xl}>The Neurodiverse <br /> travel site</h1>
        <p className={styles.mt_4}>Hotel ratings in the US based on sound, scent, texture, lighting, and staff.</p>
        <div className={styles.mt_8} ref={searchRef}>
          <div className={styles.search_container}>
            <FaSearch className={styles.search_icon} />
            <div className={styles.autocomplete_wrap}>
              <input
                type="text"
                placeholder="Search by hotel, city, state, or address"
                className={styles.inputStyle}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => inputValue.trim().length >= 2 && setSuggestionsOpen(true)}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={suggestionsOpen}
                aria-controls="hotel-search-suggestions"
                disabled={!globalData}
              />
            </div>
          </div>
          <button className={styles.searchButton} onClick={handleSearch}>Search</button>
          {(suggestionsOpen || suggestionsLoading) && (
            <ul className={styles.dropdown} id="hotel-search-suggestions" role="listbox">
              {suggestionsLoading && <li className={styles.suggestionStatus}>Searching...</li>}
              {!suggestionsLoading && suggestions.length === 0 && (
                <li className={styles.suggestionStatus}>
                  No matches for &ldquo;{inputValue.trim()}&rdquo;. Try a hotel name, city, or state.
                </li>
              )}
              {!suggestionsLoading && suggestions.map((suggestion, index) => (
                <li
                  role="option"
                  aria-selected={index === activeSuggestion}
                  className={index === activeSuggestion ? styles.activeSuggestion : ""}
                  key={`${suggestion.type}-${suggestion.id}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActiveSuggestion(index)}
                  onClick={() => selectSuggestion(suggestion)}
                >
                  <p>{suggestionLabel(suggestion)}</p>
                  {suggestionMeta(suggestion) && (
                    <span className={styles.suggestionMeta}>{suggestionMeta(suggestion)}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(HeroSection);
